// התאמת גודל טקסט אוטומטית: מרנדר את הבאנר באותו runtime (מחוץ למסך), מודד גלישה ומקטין פונט עד שנכנס.
// נעצר במינימום של המותג — אם עדיין גולש, האזהרה נשארת לבדיקה.
import type { BannerDoc } from '@bb/schema';
import { api } from '../../api';
import { ensureFonts } from './BannerView';
import type { AssetMap } from './types';

function overflows(layerEl: HTMLElement): boolean {
  const inner = layerEl.querySelector('.bb-text') as HTMLElement | null;
  if (!inner) return false;
  return inner.scrollHeight > layerEl.clientHeight + 1 || inner.scrollWidth > layerEl.clientWidth + 1;
}

export async function fitTextInDoc(doc: BannerDoc, assets: AssetMap, minFontPx: number): Promise<{ doc: BannerDoc; changed: string[]; stillOverflowing: string[] }> {
  ensureFonts(assets);
  try { await (document as any).fonts?.ready; } catch { /* לא קריטי */ }
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-20000px;top:0;visibility:hidden;pointer-events:none';
  host.setAttribute('aria-hidden', 'true');
  document.body.appendChild(host);
  const out: BannerDoc = structuredClone(doc);
  const changed: string[] = [];
  const still: string[] = [];
  try {
    for (const layer of out.layers) {
      if (layer.type !== 'text' && layer.type !== 'button') continue;
      const min = Math.max(6, minFontPx);
      let tries = 0;
      for (;;) {
        const root = document.createElement('div');
        host.replaceChildren(root);
        const ctl = (globalThis as any).BBRuntime.mount(root, out, assets, { autoplay: false });
        ctl.seek(Number.MAX_SAFE_INTEGER);
        const el = ctl.elements[layer.id] as HTMLElement | undefined;
        const over = el ? overflows(el) : false;
        ctl.destroy();
        if (!over) break;
        const next = Math.round(layer.content.fontSize * 0.92 * 10) / 10;
        if (next < min || tries++ > 14) { still.push(layer.id); break; }
        layer.content.fontSize = next;
        if (!changed.includes(layer.id)) changed.push(layer.id);
      }
    }
  } finally { host.remove(); }
  return { doc: out, changed, stillOverflowing: still };
}

/** טוען וריאציה, מתאים טקסט ושומר גרסה חדשה אם היה שינוי */
export async function autofitVariant(variantId: string): Promise<{ changed: string[]; stillOverflowing: string[] }> {
  const ctx = await api.get(`/api/variants/${variantId}`);
  if (!ctx.revision) return { changed: [], stillOverflowing: [] };
  const r = await fitTextInDoc(ctx.revision.doc, ctx.assets, ctx.kit?.minFontSizePx ?? 9);
  if (r.changed.length) {
    await api.patch(`/api/variants/${variantId}`, { expectedRevision: ctx.variant.current_revision, doc: r.doc, summary: `התאמת גודל טקסט אוטומטית: ${r.changed.join(', ')}` });
  }
  return r;
}
